import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Copy, Check } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  copied: boolean;
}

// Generate a short error ID for easier reference
function generateErrorId(): string {
  return `ERR-${Date.now().toString(36).toUpperCase()}`;
}

// Classify error type for display
function getErrorType(error: Error | null): string {
  if (!error) return 'Unknown';
  
  const name = error.name || '';
  const message = error.message || '';
  
  // Check for common iOS/Safari storage errors
  if (name === 'SecurityError' || message.includes('SecurityError')) {
    return 'SecurityError (Storage blocked)';
  }
  if (name === 'QuotaExceededError' || message.includes('QuotaExceeded')) {
    return 'QuotaExceededError (Storage full)';
  }
  if (message.includes('localStorage') || message.includes('sessionStorage')) {
    return 'StorageError';
  }
  
  // Check for chunk loading errors (common with lazy loading)
  if (message.includes('Loading chunk') || message.includes('Failed to fetch dynamically imported module')) {
    return 'ChunkLoadError (Network issue)';
  }
  
  // Check for network errors
  if (message.includes('NetworkError') || message.includes('Failed to fetch')) {
    return 'NetworkError';
  }
  
  // Check for React-specific errors
  if (message.includes('Minified React error')) {
    return 'ReactError';
  }
  
  // Return the error name if available
  return name || 'RuntimeError';
}

export class ErrorBoundary extends Component<Props, State> {
  private errorId: string;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, copied: false };
    this.errorId = '';
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.errorId = generateErrorId();
    console.error(`[ErrorBoundary] ${this.errorId} Caught error:`, error);
    console.error(`[ErrorBoundary] ${this.errorId} Error info:`, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = () => {
    // Clear any cached state that might be causing issues
    try {
      sessionStorage.clear();
    } catch (e) {
      // Ignore storage errors
    }
    window.location.reload();
  };

  handleGoBack = () => {
    window.location.href = '/auth';
  };

  handleCopyError = () => {
    const { error, errorInfo } = this.state;
    const errorType = getErrorType(error);
    const errorDetails = [
      `Error ID: ${this.errorId}`,
      `Type: ${errorType}`,
      `Message: ${error?.message || 'Unknown'}`,
      `Stack: ${error?.stack?.slice(0, 500) || 'N/A'}`,
      `Component: ${errorInfo?.componentStack?.slice(0, 300) || 'N/A'}`,
      `URL: ${window.location.href}`,
      `UA: ${navigator.userAgent}`,
      `Time: ${new Date().toISOString()}`,
    ].join('\n');

    navigator.clipboard.writeText(errorDetails).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }).catch(() => {
      // Clipboard might not be available
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const errorType = getErrorType(this.state.error);
      const isStorageError = errorType.includes('Storage') || errorType.includes('SecurityError');

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full text-center space-y-6">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-destructive" />
              </div>
            </div>
            
            <div className="space-y-2">
              <h1 className="text-xl font-semibold text-foreground">
                Something went wrong
              </h1>
              <p className="text-muted-foreground text-sm">
                {isStorageError 
                  ? 'Your browser is blocking storage access. Try disabling Private Browsing or allowing cookies.'
                  : 'An error occurred while loading the app. Please refresh the page or try again.'}
              </p>
            </div>

            {/* Always show error type for easier debugging */}
            <div className="bg-muted/50 rounded-lg p-3 text-left space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-muted-foreground">
                  {this.errorId}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={this.handleCopyError}
                >
                  {this.state.copied ? (
                    <Check className="w-3 h-3 mr-1" />
                  ) : (
                    <Copy className="w-3 h-3 mr-1" />
                  )}
                  {this.state.copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <p className="text-xs font-mono text-destructive">
                {errorType}
              </p>
              {import.meta.env.DEV && this.state.error && (
                <p className="text-xs font-mono text-muted-foreground break-all mt-2">
                  {this.state.error.message}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <Button onClick={this.handleReload} className="w-full gap-2">
                <RefreshCw className="w-4 h-4" />
                Refresh Page
              </Button>
              <Button variant="outline" onClick={this.handleGoBack} className="w-full">
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
